import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { getAllPosts } from '../lib/blog';
import { BlogPost } from '../types/blog';
import { Calendar, Clock, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';

export function BlogPage() {
  const { theme } = useTheme();
  const [posts, setPosts] = React.useState<BlogPost[]>([]);

  React.useEffect(() => {
    const loadPosts = async () => {
      const allPosts = await getAllPosts();
      setPosts(allPosts);
    };
    loadPosts();
  }, []);

  // Featured blog is the first post
  const featured = posts.length > 0 ? posts[0] : null;
  const others = posts.length > 1 ? posts.slice(1) : [];

  return (
    <div className="min-h-screen py-32">
      <div className="max-w-[1440px] mx-auto px-6">
        <div className="max-w-4xl">
          <h1 className="text-7xl font-normal mb-8">
            The latest from <span className="text-gradient">Huxley Research</span>
          </h1>
          <p className={`text-xl mb-16 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Insights and updates from our team on AI research, safety, and ethics.
          </p>
        </div>

        {/* Featured Blog */}
        {featured && (
          <article className={`group relative grid md:grid-cols-2 gap-8 p-12 rounded-3xl shadow-lg mb-24 ${
            theme === 'dark' ? 'bg-dark-200' : 'bg-white'
          }`}> 
            <div className="relative overflow-hidden rounded-2xl">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-[400px] object-cover transform group-hover:scale-105 transition-transform duration-500"
              />
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex items-center gap-4 mb-4 text-base">
                <span className={`inline-flex items-center ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Calendar className="w-5 h-5 mr-1" />
                  {format(new Date(featured.date), 'MMM d, yyyy')}
                </span>
                <span className={`inline-flex items-center ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Clock className="w-5 h-5 mr-1" />
                  {featured.readingTime}
                </span>
              </div>
              <h2 className="text-4xl font-semibold mb-6 group-hover:text-primary-500 transition-colors duration-200">
                {featured.title}
              </h2>
              <p className={`mb-8 text-lg ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div>
                  <div className="font-medium text-lg">{featured.author}</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Author
                  </div>
                </div>
              </div>
              <Link
                to={`/blog/${featured.slug}`}
                className="group inline-flex items-center text-primary-600 hover:text-primary-700 text-lg font-medium"
              >
                Read More
                <ArrowRight className="ml-2 w-6 h-6 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </article>
        )}

        {/* Other Blogs */}
        <div className="grid md:grid-cols-2 gap-12">
          {others.map((post, index) => (
            <article key={index} className={`group relative flex flex-col p-8 rounded-2xl hover-card ${
              theme === 'dark' ? 'bg-dark-300' : 'bg-white'
            }`}>
              <div className="relative overflow-hidden rounded-xl mb-6">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-[220px] object-cover transform group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex items-center gap-4 mb-2 text-sm">
                <span className={`inline-flex items-center ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Calendar className="w-4 h-4 mr-1" />
                  {format(new Date(post.date), 'MMM d, yyyy')}
                </span>
                <span className={`inline-flex items-center ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  <Clock className="w-4 h-4 mr-1" />
                  {post.readingTime}
                </span>
              </div>
              <h3 className="text-2xl font-normal mb-2 group-hover:text-primary-500 transition-colors duration-200">
                {post.title}
              </h3>
              <p className={`mb-4 ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
              }`}>
                {post.excerpt}
              </p>
              <div className="flex items-center gap-3 mb-4">
                <div>
                  <div className="font-medium">{post.author}</div>
                  <div className={`text-sm ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                  }`}>
                    Author
                  </div>
                </div>
              </div>
              <Link
                to={`/blog/${post.slug}`}
                className="group inline-flex items-center text-primary-600 hover:text-primary-700"
              >
                Read More
                <ArrowRight className="ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
              </Link>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}